import React, { useEffect, useRef, useState } from "react";
import { Button, DatePicker, Modal, Space, message } from "antd";
import { useReactToPrint } from "react-to-print";
import JsBarcode from "jsbarcode";
import moment from "moment";
import "./styles.scss";
import { ensureProductBarcode } from "../../../utils/productBarcodes";

// Printer stock is fixed; rotating the artwork must not rotate or resize the page.
const PAPER_WIDTH_IN = 2.91;
const PAPER_HEIGHT_IN = 1.26;
const WIDTH_MM = Number((PAPER_WIDTH_IN * 25.4).toFixed(3));
const HEIGHT_MM = Number((PAPER_HEIGHT_IN * 25.4).toFixed(3));
const PAGE_WIDTH_UNITS = Number((WIDTH_MM * 10).toFixed(2));
const PAGE_HEIGHT_UNITS = Number((HEIGHT_MM * 10).toFixed(2));
const ARTWORK_WIDTH_MM = 30;
// The rotated 15 x 30 mm label sits at the left short edge, centered vertically.
const ARTWORK_BOTTOM = Number(((HEIGHT_MM + ARTWORK_WIDTH_MM) * 5).toFixed(2));
const DPI = 300;
// Keep label printing independent of app styles and pin the artwork to the page origin.
const pageStyle = `
  @page { size: ${PAPER_WIDTH_IN}in ${PAPER_HEIGHT_IN}in; margin: 0; }
  @page productBarcode { size: ${PAPER_WIDTH_IN}in ${PAPER_HEIGHT_IN}in; margin: 0; }
  html, body {
    width: ${WIDTH_MM}mm; height: ${HEIGHT_MM}mm;
    margin: 0 !important; padding: 0 !important;
    min-width: 0 !important; min-height: 0 !important;
    background: white;
  }
  .product-linear-print {
    page: productBarcode;
    position: absolute; top: 0; left: 0;
    width: ${WIDTH_MM}mm; height: ${HEIGHT_MM}mm;
    margin: 0; padding: 0; box-sizing: border-box;
    overflow: hidden; break-inside: avoid;
  }
  .product-linear-label {
    display: block; width: ${WIDTH_MM}mm; height: ${HEIGHT_MM}mm;
    margin: 0; transform: none;
  }
`;

export default function ProductBarcodeLabel({ currentItem }) {
  const printRef = useRef(null);
  const barcodeRef = useRef(null);
  const pendingActionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [mfgdate, setMfgdate] = useState(null);
  const [ready, setReady] = useState(false);
  const needsDate = Boolean(currentItem.shelf_life && currentItem.expires);
  const [assignedBarcode, setAssignedBarcode] = useState(null);
  const value = assignedBarcode?.productId === currentItem.id ? assignedBarcode.code : null;
  const [allocating, setAllocating] = useState(false);
  const [allocationError, setAllocationError] = useState("");
  const [allocationAttempt, setAllocationAttempt] = useState(0);

  useEffect(() => {
    if (allocationAttempt === 0) return;
    let active = true;
    setAllocating(true);
    setAllocationError("");
    ensureProductBarcode(currentItem.id).then((code) => {
      if (active) setAssignedBarcode({ productId: currentItem.id, code });
    }).catch(() => {
      if (active) setAllocationError("Could not assign barcode. Check connection and permissions, then retry.");
    }).finally(() => { if (active) setAllocating(false); });
    return () => { active = false; };
  }, [currentItem.id, allocationAttempt]);
  const price = `PHP ${Number(currentItem.sell_price || 0).toFixed(2)}`;
  const made = needsDate && mfgdate ? `Made: ${mfgdate.format("YYYY-MM-DD")}` : "";
  const expiry = needsDate && mfgdate
    ? `Expiry: ${moment(mfgdate).add(currentItem.shelf_life, "months").format("YYYY-MM-DD")}` : "";

  useEffect(() => {
    setReady(false);
    if (!value) return;
    try {
      JsBarcode(barcodeRef.current, value, {
        format: "CODE128", width: 2, height: 60, margin: 0, marginLeft: 20, marginRight: 20,
        displayValue: false,
        background: "#ffffff", lineColor: "#000000",
      });
      // JsBarcode sets intrinsic pixel dimensions; retain its viewBox and fit the label slot.
      barcodeRef.current.setAttribute("width", "280");
      barcodeRef.current.setAttribute("height", "68");
      setReady(true);
    } catch (error) {
      message.error("Unable to generate product barcode.");
    }
  }, [value]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    copyStyles: false,
    pageStyle,
  });

  const exportPng = async () => {
    // Export the same complete SVG used for printing, including its text and margins.
    const svg = printRef.current.querySelector("svg.product-linear-label");
    const url = URL.createObjectURL(new Blob([
      new XMLSerializer().serializeToString(svg),
    ], { type: "image/svg+xml;charset=utf-8" }));
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(WIDTH_MM / 25.4 * DPI);
      canvas.height = Math.round(HEIGHT_MM / 25.4 * DPI);
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas unavailable");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      const anchor = document.createElement("a");
      anchor.download = `${String(currentItem.name || currentItem.id).replace(/[^a-z0-9_-]+/gi, "_")}_2.91x1.26in.png`;
      anchor.href = canvas.toDataURL("image/png");
      anchor.click();
      message.success("Barcode PNG exported (2.91 x 1.26 in at 300 DPI).");
    } catch (error) {
      message.error("Failed to export barcode PNG.");
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    const pending = pendingActionRef.current;
    if (ready && value && !allocating && pending?.productId === currentItem.id) {
      pendingActionRef.current = null;
      pending.execute();
    }
  }, [ready, value, allocating, currentItem.id]);

  const run = (action) => {
    if (allocating) return;
    if (needsDate && !mfgdate) {
      setVisible(true);
      return;
    }
    pendingActionRef.current = { productId: currentItem.id, execute: action === "print" ? handlePrint : exportPng };
    setAllocationAttempt((n) => n + 1);
  };

  return <>
    <div style={{ display: "none" }}>
      <div ref={printRef} className="product-linear-print">
        <svg className="product-linear-label" xmlns="http://www.w3.org/2000/svg"
          width={`${WIDTH_MM}mm`} height={`${HEIGHT_MM}mm`} viewBox={`0 0 ${PAGE_WIDTH_UNITS} ${PAGE_HEIGHT_UNITS}`}>
          <rect width={PAGE_WIDTH_UNITS} height={PAGE_HEIGHT_UNITS} fill="white" />
          <g transform={`translate(0 ${ARTWORK_BOTTOM}) rotate(-90)`}>
            <text x="150" y="26" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif"
              fontSize={Math.min(16, 260 / (Math.max(1, Math.min(String(currentItem.name || "").length, 30)) * 0.95))} fontWeight="bold">
              {String(currentItem.name || "").length > 30
                ? `${String(currentItem.name).slice(0, 27)}...` : currentItem.name}
            </text>
            <svg x="10" y="40" width="280" height={made ? "48" : "64"} viewBox="0 0 280 68" preserveAspectRatio="none">
              <svg ref={barcodeRef} x="0" y="0" preserveAspectRatio="none" />
            </svg>
            <text x="150" y={made ? "110" : "127"} textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif"
              fontSize={Math.min(16, 270 / (price.length * 0.95))} fontWeight="bold">{price}</text>
            {made && <text x="150" y="125" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontSize="9">{made}</text>}
            {expiry && <text x="150" y="138" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontSize="9">{expiry}</text>}
          </g>
        </svg>
      </div>
    </div>
    <Space wrap onClick={(e) => e.stopPropagation()}>
      <Button type="primary" disabled={allocating || Boolean(value && !ready)} onClick={() => run("print")}>Print Barcode</Button>
      <Button disabled={allocating || Boolean(value && !ready)} onClick={() => run("png")}>Export PNG</Button>
    </Space>
    {allocating && <span role="status">Preparing barcode...</span>}
    {allocationError && <div role="alert">{allocationError} <Button onClick={() => setAllocationAttempt((n) => n + 1)}>Retry</Button></div>}
    <Modal title="Manufacturing Date" visible={visible} onCancel={() => setVisible(false)}
      footer={<Space>
        <Button onClick={() => setVisible(false)}>Cancel</Button>
        <Button disabled={!mfgdate || allocating || Boolean(value && !ready)} onClick={() => run("png")}>Export PNG</Button>
        <Button type="primary" disabled={!mfgdate || allocating || Boolean(value && !ready)} onClick={() => run("print")}>Print Barcode</Button>
      </Space>}>
      <DatePicker value={mfgdate} onChange={setMfgdate} />
    </Modal>
  </>;
}
